import * as _ from 'lodash';
import L from 'leaflet';
import { reverseCoords } from '@/utils/coords';
import { shipmentStatusColorMap } from '@/config/constants';
import { getGoogleMapsLink } from '@/utils/map/google';
import { formatCurrentPositionPopup } from '@/utils/current-position-popup';
import * as newMarkersPack from '@/utils/map/new-markers';
import * as markersPack from '@/utils/map/markers';

const FALLBACK_ICON_COLOR = 'gray';

const STATUS_ICON_COLORS = {
  'In Progress': 'blue',
  Completed: 'green',
  'Completed Late': 'yellow',
  'Running Late': 'yellow',
  'Late Delivery': 'red',
  'Not Started': FALLBACK_ICON_COLOR,
  Draft: FALLBACK_ICON_COLOR,
  Cancelled: FALLBACK_ICON_COLOR,
};

function resolveIcon(status, fix, isNewIcon) {
  const iconsPack = isNewIcon ? newMarkersPack : markersPack;
  const icons =
    fix === 'gps'
      ? iconsPack['GPS_ICONS']
      : fix === 'ble'
        ? iconsPack['BLE_ICONS']
        : iconsPack['CELL_ICONS'];

  // Leaflet throws in Marker._initIcon when options.icon is undefined,
  // so an unmapped status must still resolve to an icon.
  const color = STATUS_ICON_COLORS[status] ?? FALLBACK_ICON_COLOR;
  return icons[color] ?? icons[FALLBACK_ICON_COLOR];
}

export function createMarker(shipment, options = {}) {
  const latlng = reverseCoords(shipment.current_position.location);
  const status = shipment.status;
  const fix = _.get(shipment, 'current_position.fix');
  const icon = resolveIcon(status, fix, options.isNewIcon);
  return L.marker(
    latlng,
    icon ? { id: shipment.id, icon } : { id: shipment.id },
  );
}

/** Marker for a single device position (presentation=devices). */
export function createDevicePositionMarker(shipment, position, options = {}) {
  if (!position?.location) return null;
  const latlng = reverseCoords(position.location);
  const icon = resolveIcon(shipment.status, position.fix, options.isNewIcon);
  const id = `${shipment.id}-${position.device_id}`;
  const markerOptions = icon ? { id, icon } : { id };
  if (options.pane) markerOptions.pane = options.pane;
  return L.marker(latlng, markerOptions);
}

export function getPopupContent({ shipment }) {
  const name = _.get(shipment, 'name', '-');
  const deviceName = _.get(shipment, 'device.name', '-');
  const status = _.get(shipment, 'status', null);
  const address = _.get(
    shipment,
    'current_position.address',
    'no address data',
  );
  const location = _.get(shipment, 'current_position.location', []);
  const badgeColorClass = shipmentStatusColorMap[status] ?? 'bg-gray-500';

  return `
  <div>
    <div class="font-medium text-xs">${name}</div>
    <div class="text-[10px]">
      ${deviceName}
    </div>
    <div class="text-[10px]">
      <a href=${getGoogleMapsLink({
        lat: location[1],
        lng: location[0],
      })} target="_blank">${address}</a>
    </div>
    <div class="text-[9px] inline-block whitespace-nowrap rounded py-[2px] px-1 capitalize leading-none text-white ${badgeColorClass}">
      ${status}
    </div>
  </div>
  `;
}

/** Position timestamp as Unix seconds; accepts seconds, milliseconds or ISO strings. */
function positionTimestampSec(timestamp) {
  if (typeof timestamp === 'number' && Number.isFinite(timestamp)) {
    return timestamp > 1e12 ? Math.floor(timestamp / 1000) : timestamp;
  }
  if (typeof timestamp === 'string' && timestamp.trim() !== '') {
    const asNumber = Number(timestamp);
    if (Number.isFinite(asNumber)) return positionTimestampSec(asNumber);
    const ms = Date.parse(timestamp);
    if (Number.isFinite(ms)) return Math.floor(ms / 1000);
  }
  return null;
}

/** Latest-ping popup for presentation=devices, styled like the internal shipment map. */
export function getDevicePositionPopupContent({ position, device }) {
  return formatCurrentPositionPopup({
    label: device?.name || device?.serial || null,
    address: position?.address,
    location: position?.location,
    timestampSec: positionTimestampSec(position?.timestamp),
    battery: position?.battery,
    fix: position?.fix,
    accuracyMeters: position?.accuracy,
  });
}
